import { useEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Image,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import {
  IconDownload,
  IconEye,
  IconFile,
  IconMicrophone,
} from '@/components/icons'
import { AppModal } from '@/components/ui'
import {
  formatAttachmentFileSize,
  formatAudioDuration,
  isVideoNoteAttachment,
} from '@/components/tickets/ticket-message-utils'
import { getAttachmentPath, resolveUploadsUrl } from '@/lib/uploads'
import type { TicketDetailAttachment } from '@/types/api'

type TicketMessageAttachmentsProps = {
  attachments: TicketDetailAttachment[]
  alignEnd: boolean
}

export function TicketMessageAttachments({
  attachments,
  alignEnd,
}: TicketMessageAttachmentsProps) {
  const [lightbox, setLightbox] = useState<{ src: string; title: string } | null>(null)
  const [pdfOpen, setPdfOpen] = useState<{ src: string; title: string } | null>(null)
  const [lightboxZoom, setLightboxZoom] = useState(1)
  const [lightboxOffset, setLightboxOffset] = useState({ x: 0, y: 0 })
  const [lightboxDragging, setLightboxDragging] = useState(false)
  const lightboxDragRef = useRef<{
    startX: number
    startY: number
    originX: number
    originY: number
  } | null>(null)

  useEffect(() => {
    setLightboxZoom(1)
    setLightboxOffset({ x: 0, y: 0 })
    setLightboxDragging(false)
    lightboxDragRef.current = null
  }, [lightbox?.src])

  return (
    <>
      <Stack gap="sm" mt="xs" style={{ alignItems: alignEnd ? 'flex-end' : 'flex-start' }}>
        {attachments.map((attachment) => {
          const rawPath = getAttachmentPath(attachment)
          const mediaUrl = rawPath ? resolveUploadsUrl(rawPath) : ''
          const name = attachment.original_filename || attachment.filename || 'файл'
          const size = formatAttachmentFileSize(attachment.file_size)
          const fileType = (attachment.file_type || '').toLowerCase()
          const mime = (attachment.mime_type || '').toLowerCase()
          const durationHint = formatAudioDuration(attachment.duration)

          if (!mediaUrl) {
            return (
              <Text key={attachment.id} size="xs" c="dimmed">
                {name} (файл недоступен)
              </Text>
            )
          }

          const isImage = fileType === 'image' || mime.startsWith('image/')
          if (isImage) {
            return (
              <UnstyledButton
                key={attachment.id}
                type="button"
                onClick={() => setLightbox({ src: mediaUrl, title: name })}
                style={{ display: 'block', maxWidth: 'min(100%, 280px)', textAlign: 'left', cursor: 'zoom-in' }}
              >
                <Box>
                  <Image
                    src={mediaUrl}
                    alt={name}
                    radius="sm"
                    fit="contain"
                    mah={220}
                    maw="100%"
                    style={{ border: '1px solid var(--mantine-color-default-border)' }}
                  />
                  <Group justify="space-between" gap={8} mt={4} wrap="nowrap">
                    <Text size="xs" c="dimmed" style={{ minWidth: 0 }} truncate>
                      {name}
                      {size ? ` (${size})` : ''}
                    </Text>
                    <ActionIcon
                      component="a"
                      href={mediaUrl}
                      download={name}
                      target="_blank"
                      rel="noreferrer"
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label={`Скачать ${name}`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <IconDownload size={15} stroke={1.5} />
                    </ActionIcon>
                  </Group>
                </Box>
              </UnstyledButton>
            )
          }

          const isVideo = fileType === 'video' || mime.startsWith('video/')
          if (isVideo) {
            const circle = isVideoNoteAttachment(attachment)
            return (
              <Box key={attachment.id} maw={circle ? 220 : '100%'}>
                <Text size="xs" c="dimmed" mb={4}>
                  {circle ? 'Видео-кружок' : 'Видео'}
                  {size ? ` · ${size}` : ''}
                </Text>
                <video
                  controls
                  preload="metadata"
                  src={mediaUrl}
                  style={{
                    display: 'block',
                    maxWidth: '100%',
                    maxHeight: circle ? 220 : 280,
                    borderRadius: circle ? '50%' : 8,
                    aspectRatio: circle ? '1' : undefined,
                    objectFit: circle ? 'cover' : undefined,
                    border: '1px solid var(--mantine-color-default-border)',
                    background: 'var(--mantine-color-dark-8, #1a1b1e)',
                  }}
                >
                  Ваш браузер не поддерживает видео.
                </video>
                <Text component="a" href={mediaUrl} target="_blank" rel="noreferrer" download size="xs" mt={6} c="blue">
                  Скачать
                </Text>
              </Box>
            )
          }

          const isAudio = fileType === 'audio' || mime.startsWith('audio/')
          if (isAudio) {
            return (
              <Box
                key={attachment.id}
                p="xs"
                style={{
                  borderRadius: 8,
                  border: '1px solid var(--mantine-color-default-border)',
                  background: 'light-dark(rgba(0,0,0,0.03), rgba(255,255,255,0.04))',
                  minWidth: 200,
                  maxWidth: 320,
                }}
              >
                <Group gap={8} mb={6} wrap="nowrap">
                  <IconMicrophone size={18} stroke={1.5} style={{ flexShrink: 0 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={500} lineClamp={1}>
                      Голосовое сообщение
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {name}
                      {durationHint ? ` · ${durationHint}` : ''}
                      {size ? ` · ${size}` : ''}
                    </Text>
                  </Box>
                </Group>
                <audio controls preload="metadata" src={mediaUrl} style={{ width: '100%' }}>
                  Аудио не поддерживается
                </audio>
              </Box>
            )
          }

          const isPdf = mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf')
          if (isPdf) {
            return (
              <UnstyledButton
                key={attachment.id}
                type="button"
                onClick={() => setPdfOpen({ src: mediaUrl, title: name })}
                aria-label={`Открыть PDF: ${name}`}
                p={6}
                style={{
                  display: 'block',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-default-border)',
                  maxWidth: 360,
                  background: 'light-dark(rgba(0,0,0,0.03), rgba(255,255,255,0.04))',
                }}
              >
                <Group gap={6} wrap="nowrap" align="center">
                  <IconFile size={16} stroke={1.5} style={{ flexShrink: 0 }} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={500} lineClamp={1}>
                      {name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {size ? `${size} · ` : ''}Нажмите для просмотра
                    </Text>
                  </Box>
                  <Tooltip label="Просмотр">
                    <ActionIcon
                      size="sm"
                      variant="light"
                      color="blue"
                      aria-label="Открыть PDF"
                      onClick={(event) => {
                        event.stopPropagation()
                        setPdfOpen({ src: mediaUrl, title: name })
                      }}
                    >
                      <IconEye size={16} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Скачать">
                    <ActionIcon
                      component="a"
                      href={mediaUrl}
                      download={name}
                      target="_blank"
                      rel="noreferrer"
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label="Скачать PDF"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <IconDownload size={16} stroke={1.5} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </UnstyledButton>
            )
          }

          return (
            <UnstyledButton
              key={attachment.id}
              component="a"
              href={mediaUrl}
              target="_blank"
              rel="noreferrer"
              download={name}
              style={{
                display: 'block',
                textDecoration: 'none',
                color: 'inherit',
                maxWidth: 360,
              }}
            >
              <Group
                gap={8}
                wrap="nowrap"
                p={8}
                style={{
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-default-border)',
                  background: 'light-dark(rgba(0,0,0,0.03), rgba(255,255,255,0.04))',
                }}
              >
                <IconFile size={16} stroke={1.5} style={{ flexShrink: 0 }} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="xs" fw={500} lineClamp={1}>
                    {name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {size}
                  </Text>
                </Box>
                <IconDownload size={16} stroke={1.5} style={{ opacity: 0.6, flexShrink: 0 }} />
              </Group>
            </UnstyledButton>
          )
        })}
      </Stack>

      <AppModal opened={!!lightbox} onClose={() => setLightbox(null)} size="xl" title={lightbox?.title} centered>
        {lightbox ? (
          <Stack gap="xs">
            <Group justify="center" gap={8}>
              <Button
                size="xs"
                variant="default"
                onClick={() => setLightboxZoom((zoom) => Math.max(0.25, Number((zoom - 0.25).toFixed(2))))}
              >
                -
              </Button>
              <Button
                size="xs"
                variant="default"
                onClick={() => {
                  setLightboxZoom(1)
                  setLightboxOffset({ x: 0, y: 0 })
                }}
              >
                {Math.round(lightboxZoom * 100)}%
              </Button>
              <Button
                size="xs"
                variant="default"
                onClick={() => setLightboxZoom((zoom) => Math.min(4, Number((zoom + 0.25).toFixed(2))))}
              >
                +
              </Button>
              <Button
                size="xs"
                variant="default"
                component="a"
                href={lightbox.src}
                download={lightbox.title || 'image'}
                target="_blank"
                rel="noreferrer"
                leftSection={<IconDownload size={14} stroke={1.5} />}
              >
                Скачать
              </Button>
            </Group>
            <Box
              style={{
                maxHeight: '75vh',
                overflow: 'hidden',
                position: 'relative',
                cursor: lightboxZoom > 1 ? (lightboxDragging ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
              }}
              onWheel={(event) => {
                event.preventDefault()
                setLightboxZoom((zoom) => {
                  const next =
                    event.deltaY < 0
                      ? Math.min(4, Number((zoom + 0.1).toFixed(2)))
                      : Math.max(0.25, Number((zoom - 0.1).toFixed(2)))
                  if (next <= 1) setLightboxOffset({ x: 0, y: 0 })
                  return next
                })
              }}
              onMouseDown={(event) => {
                if (event.button !== 0 || lightboxZoom <= 1) return
                event.preventDefault()
                lightboxDragRef.current = {
                  startX: event.clientX,
                  startY: event.clientY,
                  originX: lightboxOffset.x,
                  originY: lightboxOffset.y,
                }
                setLightboxDragging(true)
              }}
              onMouseMove={(event) => {
                const dragState = lightboxDragRef.current
                if (!dragState || !lightboxDragging) return
                const dx = event.clientX - dragState.startX
                const dy = event.clientY - dragState.startY
                setLightboxOffset({ x: dragState.originX + dx, y: dragState.originY + dy })
              }}
              onMouseUp={() => {
                lightboxDragRef.current = null
                setLightboxDragging(false)
              }}
              onMouseLeave={() => {
                lightboxDragRef.current = null
                setLightboxDragging(false)
              }}
            >
              <Image
                src={lightbox.src}
                alt={lightbox.title}
                fit="contain"
                mah="75vh"
                mx="auto"
                style={{
                  transform: `translate(${lightboxOffset.x}px, ${lightboxOffset.y}px) scale(${lightboxZoom})`,
                  transformOrigin: 'center center',
                  transition: lightboxDragging ? 'none' : 'transform 80ms linear',
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Stack>
        ) : null}
      </AppModal>

      <AppModal
        opened={!!pdfOpen}
        onClose={() => setPdfOpen(null)}
        size="xl"
        title={pdfOpen?.title}
        centered
        styles={{ body: { padding: 0 } }}
      >
        {pdfOpen ? (
          <iframe
            src={pdfOpen.src}
            title={pdfOpen.title}
            style={{ width: '100%', height: '75vh', border: 0, display: 'block' }}
          />
        ) : null}
      </AppModal>
    </>
  )
}
